import { useEffect, useMemo, useState, useCallback } from "react";
import { Spin } from "antd";
import { Body } from "../../../components/main-body/body";
import "./mobile-home-page.css";

import { useTranslation } from "react-i18next";
import useNotification from "../../../components/notification/notification";

import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDroppable,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import ApiHomeWidgetsManagement from "../../../api/home-widgets";
import useAdminEvents from "../../../hooks/useAdminEvents";

// Titles here are what an admin reads while deciding which sections a subject
// sees, so each one is the heading that section actually draws in the app - not
// the storage key it is filed under. The keys are frozen: they are saved per
// project in `subjectSettings.appHomeWidgets`, so renaming `overdue_tasks`
// would drop the section for every project that already has settings. The
// labels drifted from the app during LF360-1895/1931, when "Overdue tasks"
// became "Incomplete tasks" and the upcoming section became "Important
// upcoming", and this row is the only place an admin ever sees the old words.
const WIDGET_META = {
  last_lifebot_message: {
    title: "Last LifeBot message",
    description: "Shows the last message from LifeBot on the Home screen.",
  },
  life_points: {
    title: "Life points",
    description: "Shows current Life Points on the Home screen.",
  },
  // Key `overdue_tasks`, heading "Incomplete tasks" - see the note above.
  overdue_tasks: {
    title: "Incomplete tasks",
    description:
      "Shows a count of tasks from earlier days that the subject can still complete, opening the full list day by day. This is where the daily incomplete-tasks reminder lands.",
  },
  daily_tasks: {
    title: "Tasks for today",
    description: "Shows daily tasks widget on the Home screen.",
  },
  // Not "the week ahead": the notification rule has no upper date bound, so a
  // reminder configured 14 days out puts its task here 14 days out.
  upcoming_tasks: {
    title: "Important upcoming",
    description:
      "Shows tasks the subject has already been told about but cannot open yet, however far ahead they are scheduled.",
  },
  feed: {
    title: "Feed",
    description: "Shows the content feed widget on the Home screen.",
  },
};

const ACTIVE_CONTAINER_ID = "active_container";
const INACTIVE_CONTAINER_ID = "inactive_container";

function DroppableColumn({ id, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`mhp-dropzone ${isOver ? "mhp-dropzone--over" : ""}`}
    >
      {children}
    </div>
  );
}

// 6-dot grip glyph — conventional drag-handle indicator. Hidden by default,
// fades in on row hover when the page is in edit mode (see CSS).
function DragGripIcon() {
  return (
    <svg
      width="10"
      height="16"
      viewBox="0 0 10 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="2" cy="3" r="1.25" fill="currentColor" />
      <circle cx="2" cy="8" r="1.25" fill="currentColor" />
      <circle cx="2" cy="13" r="1.25" fill="currentColor" />
      <circle cx="8" cy="3" r="1.25" fill="currentColor" />
      <circle cx="8" cy="8" r="1.25" fill="currentColor" />
      <circle cx="8" cy="13" r="1.25" fill="currentColor" />
    </svg>
  );
}

function WidgetCard({ id, isEditing }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id,
      disabled: !isEditing,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const meta = WIDGET_META[id] || { title: id, description: "" };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mhp-item ${isEditing ? "mhp-item--draggable" : ""}`}
      {...attributes}
      {...listeners}
    >
      {isEditing && (
        <span className="mhp-item__grip" aria-hidden="true">
          <DragGripIcon />
        </span>
      )}
      <div className="mhp-item__body">
        <div className="mhp-item__title">{meta.title}</div>
        {!!meta.description && <div className="mhp-item__desc">{meta.description}</div>}
      </div>
    </div>
  );
}

function Column({ title, description, items, isEditing }) {
  return (
    <div className="mhp-col">
      <div className="mhp-col__header">
        <div className="mhp-col__title">{title}</div>
        {!!description && <div className="mhp-col__desc">{description}</div>}
      </div>

      <div className="mhp-list">
        {items.map((id) => (
          <WidgetCard key={id} id={id} isEditing={isEditing} />
        ))}
        {items.length === 0 && <div className="mhp-empty">Empty</div>}
      </div>
    </div>
  );
}

function OverlayCard({ id }) {
  if (!id) return null;
  const meta = WIDGET_META[id] || { title: id, description: "" };

  return (
    <div className="mhp-item mhp-item--overlay">
      <span className="mhp-item__grip mhp-item__grip--visible" aria-hidden="true">
        <DragGripIcon />
      </span>
      <div className="mhp-item__body">
        <div className="mhp-item__title">{meta.title}</div>
        {!!meta.description && <div className="mhp-item__desc">{meta.description}</div>}
      </div>
    </div>
  );
}

export default function MobileHomePage() {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [active, setActive] = useState([]);
  const [inactive, setInactive] = useState([]);
  const [initial, setInitial] = useState(null);

  const [dragId, setDragId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const dirty = useMemo(() => {
    if (!initial) return false;
    return JSON.stringify({ active, inactive }) !== JSON.stringify(initial);
  }, [active, inactive, initial]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ApiHomeWidgetsManagement.getHomeWidgets();
      const hw = res?.homeWidgets || { active: [], inactive: [] };

      setActive(hw.active || []);
      setInactive(hw.inactive || []);
      setInitial({ active: hw.active || [], inactive: hw.inactive || [] });

      setIsEditing(false);
    } catch (e) {
      openNotification({
        type: "error",
        message: <p>{t("home_widget.notification.failed_load")}</p>,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useAdminEvents({
    onSettingChanged: (payload) => {
      if (payload?.group === "homeWidgets") {
        load();
      }
    },
  });

  const findContainer = (id) => {
    if (id === ACTIVE_CONTAINER_ID) return "active";
    if (id === INACTIVE_CONTAINER_ID) return "inactive";
    if (active.includes(id)) return "active";
    if (inactive.includes(id)) return "inactive";
    return null;
  };

  const onDragStart = (event) => {
    if (!isEditing) return;
    setDragId(event?.active?.id || null);
  };

  const onDragCancel = () => {
    setDragId(null);
  };

  const onDragEnd = (event) => {
    if (!isEditing) {
      setDragId(null);
      return;
    }

    const { active: dragActive, over } = event;
    if (!over) {
      setDragId(null);
      return;
    }

    const id = dragActive.id;
    const overId = over.id;

    const from = findContainer(id);
    const to = findContainer(overId);

    if (!from || !to) {
      setDragId(null);
      return;
    }

    const overIsContainer =
      overId === ACTIVE_CONTAINER_ID || overId === INACTIVE_CONTAINER_ID;

    // reorder in same column (only when dropping over another item)
    if (from === to && !overIsContainer) {
      if (to === "active") {
        const oldIndex = active.indexOf(id);
        const newIndex = active.indexOf(overId);
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          setActive(arrayMove(active, oldIndex, newIndex));
        }
      } else {
        const oldIndex = inactive.indexOf(id);
        const newIndex = inactive.indexOf(overId);
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          setInactive(arrayMove(inactive, oldIndex, newIndex));
        }
      }
      setDragId(null);
      return;
    }

    // move across columns OR drop to end by dropping on container
    if (from !== to) {
      const fromList = from === "active" ? active : inactive;
      const toList = to === "active" ? active : inactive;

      if (!fromList.includes(id)) {
        setDragId(null);
        return;
      }

      const nextFrom = fromList.filter((x) => x !== id);
      const nextTo = [...toList];

      const insertAt = overIsContainer ? nextTo.length : nextTo.indexOf(overId);
      nextTo.splice(insertAt === -1 ? nextTo.length : insertAt, 0, id);

      if (from === "active") setActive(nextFrom);
      else setInactive(nextFrom);

      if (to === "active") setActive(nextTo);
      else setInactive(nextTo);

      setDragId(null);
      return;
    }

    // same column + drop on container => do nothing
    setDragId(null);
  };

  const onToggleEdit = useCallback(() => {
    setIsEditing((s) => !s);
  }, []);

  const onCancelEdit = useCallback(() => {
    if (!initial) return;
    setActive(initial.active);
    setInactive(initial.inactive);
    setIsEditing(false);
  }, [initial]);

  const onSave = useCallback(async () => {
    try {
      setSaving(true);
      const payload = { active, inactive };
      const res = await ApiHomeWidgetsManagement.updateHomeWidgets(payload);

      const hw = res?.homeWidgets || payload;
      const nextInitial = {
        active: hw.active || active,
        inactive: hw.inactive || inactive,
      };

      setInitial(nextInitial);
      setIsEditing(false);
      openNotification({
        type: "success",
        message: <p>{t("home_widget.notification.success")}</p>,
      });
    } catch (e) {
      const code = e?.response?.data?.error?.code || e?.response?.data?.code;
      if (code === "INVALID_HOME_WIDGETS") {
        openNotification({
          type: "error",
          message: <p>{t("home_widget.notification.invalid_config")}</p>,
        });
      } else {
        openNotification({
          type: "error",
          message: <p>{t("home_widget.notification.error")}</p>,
        });
      }
    } finally {
      setSaving(false);
    }
  }, [active, inactive]);

  const headerConfig = useMemo(
    () => ({
      title: "Mobile home page",
      editable: true,
      isEditing,
      onToggleEdit,
      onSave,
      onCancelEdit,
      saveDisabled: !isEditing || saving || !dirty,
    }),
    [isEditing, onToggleEdit, onSave, onCancelEdit, saving, dirty]
  );

  return (
    <Body header={headerConfig}>
      <div className="mhp-container">
        <div className="mhp-help">
          Drag widgets between columns to enable/disable. Order in “Active widgets” defines the Home screen order.
        </div>

        {loading ? (
          <div style={{ paddingTop: 12 }}>
            <Spin />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragCancel={onDragCancel}
            onDragEnd={onDragEnd}
          >
            <div className="mhp-grid">
              <SortableContext items={active} strategy={verticalListSortingStrategy}>
                <DroppableColumn id={ACTIVE_CONTAINER_ID}>
                  <Column
                    title="Active widgets"
                    description="This order defines the layout on the Mobile Home screen."
                    items={active}
                    isEditing={isEditing}
                  />
                </DroppableColumn>
              </SortableContext>

              <SortableContext items={inactive} strategy={verticalListSortingStrategy}>
                <DroppableColumn id={INACTIVE_CONTAINER_ID}>
                  <Column
                    title="Inactive widgets"
                    description="These widgets are hidden on the Mobile Home screen."
                    items={inactive}
                    isEditing={isEditing}
                  />
                </DroppableColumn>
              </SortableContext>
            </div>

            <DragOverlay>
              <OverlayCard id={dragId} />
            </DragOverlay>
          </DndContext>
        )}
      </div>
    </Body>
  );
}
